import React, { FC, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { getSpotPermissions } from 'lib/spot-permissions';
import { getPermissions } from 'lib/permissions';
import { useStamps } from 'lib/stamps';
import { calcHouManyHoursAgo } from 'lib/calc-time';

import { useSpots } from '../../../lib/spots';
import { UserPage } from "components/UserPage";
import { Section } from "components/Section";
import { BreadCrumbs } from 'components/BreadCrumbs';


type Props = {};

export const SpotUserListPage: FC<Props> = (props) => {
    // @ts-ignore
    const id = props.match.params.id;
    const { get } = useSpots();
    const [spot, setSpot] = useState<Spot | null>(null);
    const [users, setUsers] = useState<CheckinUser[]>([]);
    const [page, setPage] = useState(1);
    const [order, setOrder] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const hasMoreRef = useRef(true);
    const loadingRef = useRef(false);
    const loader = useRef<HTMLDivElement>(null);
    const timer = useRef<any>(null);
    const { summary } = useStamps();
    const history = useHistory();

    useEffect(() => {
        async function initialize() {
            const spot = await get(id);
            setSpot(spot);
        };
        initialize();
    }, []);

    useEffect(() => {
        async function initialize() {
            const spotPermissions = await getSpotPermissions(id);
            const userPermission = await getPermissions();
            if (!spotPermissions.view) {
                if (userPermission.status === 'unpaid') {
                    history.push(
                        `/map/spot/${id}`,
                        {
                            flash: 'サービスをご利用になるには未払い金をお支払いください。',
                            error: true,
                        },
                    );
                }
                return;
            }

            if (loadingRef.current) {
                return;
            }

            loadingRef.current = true;
            setLoading(true);
            const res = await summary(Number(id), order, page);

            if (res.length < 1) {
                hasMoreRef.current = false;
                setHasMore(false);
                loadingRef.current = false;
                setLoading(false);
                window.removeEventListener('scroll', onScroll);
                return;
            }
            setUsers([...users, ...res]);
            loadingRef.current = false;
            setLoading(false);
        };
        initialize();
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        }
    }, [page, order]);

    const toggleDateOrder = () => {
        switch (order) {
            case 1:
                setOrder(2);
                break;
            case 2:
                setOrder(1);
                break;
            default:
                setOrder(1);
                break;
        }
        setPage(1);
        setUsers(prev => []);
        hasMoreRef.current = true;
        setHasMore(true);
    };
    const toggleCountOrder = () => {
        switch (order) {
            case 3:
                setOrder(4);
                break;
            case 4:
                setOrder(3);
                break;
            default:
                setOrder(3);
                break;
        }
        setPage(1);
        setUsers(prev => []);
        hasMoreRef.current = true;
        setHasMore(true);
    };
    const onScroll = () => {
        if (timer.current) {
            clearTimeout(timer.current);
        }

        timer.current = setTimeout(() => {
            const margin = 100;
            const clientHeight = document.documentElement.clientHeight;
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;

            if (!hasMoreRef.current || loadingRef.current ||
                (loader.current && loader.current.offsetTop > scrollTop + clientHeight + margin)
            ) {
                return;
            }

            setPage(prev => prev + 1);
        }, 200);
    };

    return (
        <UserPage title="Salon.jp | スタンプ獲得ユーザー一覧" disableBackButton>
            <Section fillOnSp={true}>
                {spot && (
                    <div className="bread-crumbs-wrapper">
                        <BreadCrumbs items={[
                            {
                                title: 'お店の一覧',
                                href: '/map/my_list',
                            },
                            {
                                title: spot.name,
                                href: `/map/spot/${id}`,
                            },
                            {
                                title: 'お店管理',
                                href: `/map/spot/${id}/admin`,
                            },
                            {
                                title: 'お客様一覧',
                                href: `/map/spot/${id}/users`,
                            },
                        ]} />
                    </div>
                )}
                <div className="wrap">
                    <div className="head">
                        <img src="/img/map_admin/customer.png" alt="" />
                        <h1 className="title">お客さま一覧</h1>
                    </div>

                    <div className="actions">
                        <span
                            className={['item', (order === 1) ? '-desc' : '', (order === 2) ? '-asc' : ''].join(' ')}
                            onClick={toggleDateOrder}>最新順</span>
                        <span
                            className={['item', (order === 3) ? '-desc' : '', (order === 4) ? '-asc' : ''].join(' ')}
                            onClick={toggleCountOrder}>回数順</span>
                    </div>

                    <div className="list">
                        {users.map((user) => (
                            <li className="item" key={user.id}>
                                <img className="image" width={100} height={100} src={user.fb_id ? `/user_icons/get/dummy?user_id=${user.id}` : `${user.icon_path}`} alt="" />
                                <div className="desc">
                                    {user.is_public ? (
                                        <a className="user-name" href={`/members/view/${user.short_id}`}>{user.fb_name}</a>
                                    ) : (
                                        <div className="user-name">{user.fb_name}</div>
                                    )}
                                    <div className="date">最終チェックイン:&nbsp;{calcHouManyHoursAgo(user.last_checkin_date)}</div>
                                </div>
                                <div>{user.checkin_count}回</div>
                            </li>
                        ))}
                    </div>
                    {hasMore && (
                        <div ref={loader} className="loader" key={0}>
                            {loading && (
                                <img src="/img/loading.gif" />
                            )}
                        </div>
                    )}
                </div>
            </Section>

            <style jsx>{`
                .bread-crumbs-wrapper {
                    padding-top: 10px;
                    padding-bottom: 20px;
                }
                .wrap {
                    min-height: 50vh;
                    padding: 1rem;
                    padding-top: 0px;
                }
                .head {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .head > .title {
                    font-size: 1rem;
                    padding-left: 0.5rem;
                }
                .actions {
                    display: flex;
                    justify-content: center;
                    margin-bottom: 0.5rem;
                }
                .actions > .item + .item {
                    padding-left: 1rem;
                }
                .actions > .item.-asc,
                .actions > .item.-desc {
                    font-weight: bold;
                }
                .actions > .item.-asc:after,
                .actions > .item.-desc:after {
                    padding-left: 0.2rem;
                    font-size: 0.8rem;
                }
                .actions > .item.-asc:after {
                    content: '▲';
                }
                .actions > .item.-desc:after {
                    content: '▼';
                }
                .list {
                    margin-left: 0;
                    padding-left: 0;
                    border-top: 2px solid #aaaaaa;
                    line-style: none;
                }
                .list > .item {
                    display: flex;
                    jsutify-content: space-between;
                    align-items: center;
                    padding: 1rem 0.5rem 1rem 0;
                    border-bottom: 1px solid #a8a8a8;
                }
                .item > .image {
                    border-radius: 50%;
                    width: 50px;
                    height: 50px;
                }
                .item > .desc {
                    flex-grow: 1;
                    padding-left: 1rem;
                }
                .item > .desc > .date {
                    padding-top: 0.5rem;
                    font-size: 0.7rem;
                    color: #a8a8a8;
                }
                .item > .desc {
                    color: #000000;
                }
                .desc > a:link,
                .desc > a:visited,
                .desc > a:hover,
                .desc > a:active {
                    color: #ed7c4f;
                }
                .user-name {
                    font-weight: bold;
                }
                .loader {
                    margin-top: 10px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                @media screen and (max-width: 540px) {
                    .wrap {
                        min-height: 100vh;
                    }
                }
            `}</style>
        </UserPage>
    );
};
